import { useWalletFanToken } from '@capsicum/features/wallet/shared';
import { type NCWBalanceType, usePartners } from '@capsicum/socios-core';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useRef, useState } from 'react';

import { setProductQuantityAtom } from '../../data-access';
import { useProduct } from '../useProduct';

import { useProductCard } from './useProductCard';

type UseProductPricingProps = {
	getTokenBalanceBy?: NCWBalanceType['getTokenBalanceBy'];
	variantId: string;
};

export const useProductPricing = ({ getTokenBalanceBy = () => undefined, variantId }: UseProductPricingProps) => {
	const refPrice = useRef<number>(0);
	const [isBalanceBelowZero, setIsBalanceBelowZero] = useState<boolean>(false);
	const [balanceAfterRedeem, setBalanceAfterRedeem] = useState<number>(0);

	const { getSsuAmount, getTokenRequired, variant } = useProduct({
		variantId
	});

	const { balance } = useProductCard({
		getTokenBalanceBy,
		variant
	});

	const { getPartnerBy } = usePartners();
	const ssuPartner = useMemo(() => getPartnerBy(p => p.shortName === 'SSU')?.currencyId, [getPartnerBy]);
	const partnerCurrencyId = useMemo(
		() => getPartnerBy(p => p.id === variant?.product?.partnerId)?.currencyId,
		[getPartnerBy, variant?.product?.partnerId]
	);
	const { data: ssuWallet } = useWalletFanToken(ssuPartner);
	const { data: partnerWallet } = useWalletFanToken(partnerCurrencyId);

	const { totalBalance } = ssuWallet ?? {};

	const [quantity, setQuantity] = useAtom(setProductQuantityAtom);
	const isPackageProduct = variant?.product?.isPackage;
	const packageQuantity = variant?.product?.quantityStep ?? 2;
	const productQuantity = packageQuantity * quantity;
	const packageProductPrice = useMemo(() => getSsuAmount() * packageQuantity, [getSsuAmount, packageQuantity]);

	refPrice.current = getSsuAmount();
	const [totalPrice, setTotalPrice] = useState<number>(0);

	const tokensRequired = getTokenRequired();

	const hasEnoughStock = useMemo(() => (variant?.availableStock || 0) > 0, [variant]);

	const isEnoughBalance = useMemo(() => balanceAfterRedeem - refPrice.current >= 0, [balanceAfterRedeem]);

	const isRedeemEnabledOrderSummary = useMemo(
		() => balanceAfterRedeem >= 0 && totalPrice !== 0 && !isBalanceBelowZero,
		[balanceAfterRedeem, totalPrice, isBalanceBelowZero]
	);

	const amountNeeded = useMemo(
		() => (tokensRequired - balance > 0 ? tokensRequired - balance : 0),
		[tokensRequired, balance]
	);

	const hasEnoughSsu = useMemo(() => totalBalance && totalBalance >= getSsuAmount(), [getSsuAmount, totalBalance]);

	useEffect(() => {
		if (isPackageProduct) {
			setTotalPrice(refPrice.current * (packageQuantity || 1));
		} else {
			setTotalPrice(refPrice.current * quantity);
		}
	}, [isPackageProduct, packageQuantity, quantity]);

	useEffect(() => {
		if (totalBalance! - totalPrice < 0) {
			setIsBalanceBelowZero(true);
			setBalanceAfterRedeem(0);

			return;
		} else if (totalBalance! - totalPrice === 0) {
			setIsBalanceBelowZero(false);
		}
		setIsBalanceBelowZero(false);
		setBalanceAfterRedeem(totalBalance! - totalPrice);
	}, [totalBalance, totalPrice]);

	return {
		amountNeeded,
		balanceAfterRedeem,
		getSsuAmount,
		hasEnoughSsu,
		hasEnoughStock,
		isBalanceBelowZero,
		isEnoughBalance,
		isPackageProduct,
		isRedeemEnabledOrderSummary,
		packageProductPrice,
		packageQuantity,
		partnerWallet,
		productQuantity,
		quantity,
		refPrice,
		setQuantity,
		tokensRequired,
		totalPrice
	};
};
